// utils/fetchInterceptor.js
// Detects a dead Azure SWA auth session and stops the app cleanly instead of
// letting a raw HTML error page leak into an alert().
//
// THE BUG THIS FIXES
// staticwebapp.config.json gates /api/* on allowedRoles:["authenticated"]. When
// the StaticWebAppsAuthCookie expires (~8h), the SWA *edge* rejects the request
// with an HTML 401 page and the Azure Function never runs — so nothing lands in
// rprs.AuthLog or Feedback.ServerErrors, and the SPA has no idea. A user left a
// tab open overnight and got this on Save the next morning:
//   alert("Save failed: schedWrite(get_batch_id) 401: <!DOCTYPE html>…")
// Meanwhile every read path swallows the same 401 silently (the 60s notification
// poller in useSchedulerData does .catch(() => [])), so the grid kept rendering
// a fully populated, logged-in-looking page from stale React state all night.
//
// WHY window.fetch AND NOT THE SERVICE WRAPPERS
// Nine call sites do raw inline fetch('/api/…') and bypass schedApi/schedData
// entirely — three of them (TokenLoginAwarder, SchedulerGrid's notify_move,
// reconcileOutlook) swallow errors completely. Patching only the shared helpers
// would leave exactly the invisible failures invisible. One global patch covers
// every call site, including ones written later.
//
// Idiom matches utils/bugLog.js: module-scope guard, install…() called from
// index.js before React renders. The original fetch is always called through,
// and successful responses are returned untouched.

import { isLocalDev } from '../auth/AuthContext';
import { recordEvent } from './bugLog';

let installed = false;

// Latched the moment we see the edge 401. Never reset: the only way out of this
// state is a full page navigation (sign-in), which reloads the module graph.
let sessionExpired = false;

// Lets a caller suppress its own error UI once the global handler has claimed
// the failure. Not needed by anything today (halting the request is what keeps
// the alert quiet — see below), but cheap and useful for future call sites.
export function isSessionExpired() {
  return sessionExpired;
}

// fetch()'s first arg can be a string, a URL, or a Request.
function resolveUrl(input) {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  if (input && typeof input === 'object' && 'url' in input) return input.url;
  return String(input);
}

// Same-origin /api/* only. Naturally excludes /.auth/* — which never 401s
// anyway; SWA returns 200 with clientPrincipal:null for an anonymous session.
function isApiCall(urlStr) {
  try {
    const u = new URL(urlStr, window.location.origin);
    return u.origin === window.location.origin && u.pathname.startsWith('/api/');
  } catch {
    return false;
  }
}

// Not every 401 means the session died. api/shared/tokenProxy.js returns a JSON
// 401 ({error:'Not signed in.'}) when the principal header is missing, and 18
// endpoints route through it (bug-report, suggestions, tokens-*). The SWA edge
// page is text/html — that's the discriminator.
//
// Header-only on purpose: reading the body here would consume the stream and
// break every caller that does its own r.text()/r.json(). No clone() needed.
function isEdgeAuthFailure(response) {
  if (response.status !== 401) return false;
  const ct = (response.headers.get('content-type') || '').toLowerCase();
  return ct.includes('text/html');
}

// A promise that never settles. Handing this back instead of the 401 Response
// is what suppresses the raw HTML alert: schedWrite's `throw` never runs, so
// SchedulerGrid's catch never runs, so alert('Save failed: <!DOCTYPE html>…')
// never fires. The user sees only the SessionExpiredModal.
//
// Trade-off, accepted: an in-flight spinner (setModalSaving(true)) keeps
// spinning behind the blocking modal, and `finally` blocks don't run. That's
// correct rather than leaky — the page is terminal, every /api/* call is dead,
// and the only path forward is the reload that signing back in triggers.
function halt() {
  return new Promise(() => {});
}

function handleExpiry() {
  if (sessionExpired) return;
  sessionExpired = true;

  // Set synchronously above so the ~10 parallel reads in useSchedulerData's
  // Promise.all dispatch this exactly once.
  try {
    window.dispatchEvent(new CustomEvent('app:session-expired'));
  } catch { /* no-op */ }

  if (isLocalDev()) {
    console.warn('[session] SWA edge returned an HTML 401 — session expired. Not redirecting in local dev.');
  }
}

export function installSessionExpiryInterceptor() {
  if (installed || typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async function interceptedFetch(input, init) {
    // Already dead: short-circuit without touching the network, so the 60s
    // poller and any retry loop stop hammering a session that can't recover.
    if (sessionExpired) {
      try {
        if (isApiCall(resolveUrl(input))) return halt();
      } catch { /* fall through to the real fetch */ }
    }

    let response;
    try {
      response = await originalFetch(input, init);
    } catch (err) {
      // Network-level failure (offline, DNS, aborted, CORS). Breadcrumb it before
      // rethrowing so the caller's own error handling is completely unchanged.
      try {
        const url = resolveUrl(input);
        if (isApiCall(url)) {
          recordEvent('api.network-error', {
            url: new URL(url, window.location.origin).pathname + new URL(url, window.location.origin).search,
            method: (init && init.method) || 'GET',
            message: err?.message || String(err),
          });
        }
      } catch { /* no-op */ }
      throw err;
    }

    // Detection must never break the caller — any throw in here would surface
    // as a failed request for an otherwise fine response.
    try {
      if (isEdgeAuthFailure(response) && isApiCall(resolveUrl(input))) {
        recordEvent('api.session-expired', { url: resolveUrl(input) });
        handleExpiry();
        return halt();
      }

      // Breadcrumb every failed /api/* call. This is the signal the bug reports
      // have been missing: the read paths do `.catch(() => [])` and several inline
      // fetches swallow errors outright, so a user can watch a page render wrong
      // with nothing anywhere to show a request 500'd. Response is returned
      // untouched — this only observes.
      if (!response.ok && isApiCall(resolveUrl(input))) {
        const u = new URL(resolveUrl(input), window.location.origin);
        recordEvent('api.error', {
          url: u.pathname + u.search,
          method: (init && init.method) || 'GET',
          status: response.status,
        });
      }
    } catch { /* no-op */ }

    return response;
  };
}
